import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';
import { useProjectScope } from '../lib/projectScope.js';

/**
 * Topbar project-scope switcher (ADR-0029). Sets the global "current project"
 * that Dashboard, Posts, and Inbox filter to. Renders nothing useful until
 * projects load; collapses to a plain label when the org has a single project.
 */
export function ProjectScopeSwitcher() {
  const [scope, setScope] = useProjectScope();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const q = useQuery({
    queryKey: ['projects', 'scope-switcher'],
    queryFn: ({ signal }) => apiGet('/projects', { signal }),
    staleTime: 60_000,
  });
  const projects = q.data?.projects || [];

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // If the stored scope points at a project that no longer exists, clear it.
  useEffect(() => {
    if (scope && projects.length && !projects.some((p) => p.key === scope)) setScope('');
  }, [scope, projects, setScope]);

  if (projects.length <= 1) return null; // nothing to switch between

  const current = projects.find((p) => p.key === scope);
  const label = current ? current.display_name : 'All projects';

  return (
    <div className="proj-scope" ref={ref}>
      <button
        type="button"
        className={`proj-scope-trigger${scope ? ' scoped' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Filter the app to one project"
      >
        <span className="proj-scope-dot" aria-hidden="true">▾</span>
        <span className="proj-scope-label">{label}</span>
      </button>
      {open && (
        <ul className="proj-scope-menu" role="listbox">
          <li role="option" aria-selected={!scope}>
            <button type="button" className={!scope ? 'active' : ''} onClick={() => { setScope(''); setOpen(false); }}>
              All projects
            </button>
          </li>
          {projects.map((p) => (
            <li key={p.key} role="option" aria-selected={scope === p.key}>
              <button type="button" className={scope === p.key ? 'active' : ''} onClick={() => { setScope(p.key); setOpen(false); }}>
                {p.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
